/**
 * Loyalty Store - manages loyalty program vouchers, rewards, activity history, and athlete/influencer credit payments.
 *
 * **Key responsibilities:**
 * - Fetch and cache loyalty vouchers (points-based rewards)
 * - Manage reward codes and reward cards (payment instruments)
 * - Calculate current reward value based on points balance
 * - Fetch paginated loyalty activity history
 * - Track athlete credit and influencer credit payment eligibility
 * - Determine if loyalty rewards payment is allowed (blocked by athlete/influencer credit)
 *
 * **Loyalty program features:**
 * - **Vouchers**: Points-based rewards redeemable for discounts
 * - **Reward codes**: One-time use promo codes from loyalty
 * - **Reward cards**: Gift cards earned through loyalty
 * - **Activity history**: Loyalty ledger entries for points, meters, and credits
 * - **Athlete credit**: Annual credit for sponsored athletes (blocks loyalty rewards)
 * - **Influencer credit**: Annual credit for IPA/prosumers (blocks loyalty rewards)
 *
 * **Multi-brand notes:**
 * - Loyalty features controlled by LaunchDarkly flags (enableLoyaltyFeatures)
 * - Voucher structure may vary by brand
 * - Point values and reward tiers brand-specific
 * - The North Face: VF Pass loyalty program
 * - Other brands: varied loyalty implementations
 *
 * **Payment priority:**
 * 1. Athlete credit (if applicable, blocks rewards)
 * 2. Influencer/IPA credit (if applicable, blocks rewards)
 * 3. Loyalty rewards (vouchers, codes, cards)
 *
 * **Integration points:**
 * - Cart store: payment methods and totals
 * - User store: athlete/IPA profile flags
 * - Checkout: payment instrument selection
 *
 * **Performance:**
 * - Vouchers loaded lazily (`immediate: false`)
 * - Activity history loaded lazily and appended page by page
 * - Reward codes/cards fetched on demand
 * - Cached until explicitly reset (logout, order placement)
 *
 * **Critical composable rules:**
 * - ⚠️ **Always call in component setup** (not inside methods/callbacks)
 * - ⚠️ **Cache store reference**, don't call useLoyaltyStore() repeatedly
 *
 * @see domains/commerce/stores/cart.ts for payment methods
 * @see domains/commerce/stores/user.ts for athlete/IPA profiles
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */
export const useLoyaltyStore = defineStore('loyalty', () => {
  const { $formatPrice } = useNuxtApp()
  const cart = useCartStore()
  const user = useUserStore()
  const { consumer } = useApi()
  const { hideEmployeePoints } = useAppConfig().components.vf.loyalty

  // ────────────────────────────────────────────────────────────────────────────
  // Vouchers (Points-Based Rewards)
  // ────────────────────────────────────────────────────────────────────────────

  const {
    data: vouchers,
    error: vouchersError,
    status: vouchersStatus,
    pending: vouchersPending,
    execute: fetchVouchers
  } = consumer.getLoyaltyVouchers({ immediate: false })

  const getVouchers = async () => {
    if (!user.isEmployeeProfile || !hideEmployeePoints) {
      await fetchVouchers()
    }
  }

  /**
   * Current reward value user can redeem based on points balance.
   * - Finds highest applicable voucher tier
   * - Returns formatted price string
   *
   * @example
   * ```ts
   * // User has 500 points
   * // Vouchers: 100pts=$5, 300pts=$15, 500pts=$25
   * // Returns: "$25"
   * ```
   */
  const currentReward = computed(() => {
    const currentPoints = vouchers.value?.currentLoyaltyPoints || 0
    const amount = vouchers.value?.applicableLoyaltyVouchers?.reduce((acc, voucher) =>
      currentPoints >= voucher.points ? Number(voucher.amountInCurrency) : acc, 0) || 0

    return $formatPrice(amount, useRuntimeConfig().public.currency[useLocale()], { minimumFractionDigits: 0 })
  })

  /**
   * Whether any loyalty voucher is blocked by product exclusion rules.
   * - Matches both direct and combined ineligibility reasons
   * - Useful for hiding redemption UI when the cart contains excluded items
   */
  const hasProductExclusion = computed(() =>
    vouchers.value?.applicableLoyaltyVouchers?.some(({ reasonForIneligibility }) =>
      reasonForIneligibility?.includes('PRODUCT_EXCLUSION')) || false
  )

  /**
   * Highest loyalty voucher amount currently available for redemption.
   * - Considers only vouchers marked as available true
   * - Returns raw numeric amount for downstream payment checks
   */
  const highestAvailableVoucher = computed(() => {
    const amounts = vouchers.value?.applicableLoyaltyVouchers?.filter(({ available }) => available)
      .map((voucher) => Number(voucher.amountInCurrency))

    return amounts?.length ? Math.max(...amounts) : 0
  })

  /**
   * Available loyalty balance based on the current loyalty points.
   * - Converts points to a balance using the loyalty rate
   * - Returns raw numeric amount
   */
  const availableBalance = computed(() => (vouchers.value?.currentLoyaltyPoints || 0) * 0.005)

  // ────────────────────────────────────────────────────────────────────────────
  // Activity History
  // ────────────────────────────────────────────────────────────────────────────

  const activitiesPage = ref(1)
  const activitiesPerPage = 5
  const activitiesPeriod = 'alltime'

  type ActivitiesResponse = Awaited<ReturnType<typeof consumer.$getLoyaltyActivities>>['data']
  type Activity = ActivitiesResponse['activities'][number]

  const activities = ref<Activity[]>([])
  const activitiesPending = ref(false)
  const activitiesResponse = ref<ActivitiesResponse>()

  const hasMoreActivities = computed(() =>
    activitiesPage.value < (activitiesResponse.value?.total_pages ?? 0)
  )

  const fetchActivities = async () => {
    activitiesPending.value = true

    try {
      const response = await consumer.$getLoyaltyActivities({
        page: activitiesPage.value,
        per_page: activitiesPerPage,
        period: activitiesPeriod
      })

      activitiesResponse.value = response.data
      return true
    }
    catch (err) {
      log.error(err)
      return false
    }
    finally {
      activitiesPending.value = false
    }
  }

  const getActivities = async () => {
    activitiesPage.value = 1

    if (await fetchActivities())
      activities.value = activitiesResponse.value?.activities ?? []
  }

  const loadMoreActivities = async () => {
    if (activitiesPending.value || !hasMoreActivities.value) return

    const currentPage = activitiesPage.value
    activitiesPage.value += 1

    if (!await fetchActivities()) {
      activitiesPage.value = currentPage
      return
    }

    activities.value = [...activities.value, ...(activitiesResponse.value?.activities ?? [])]
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Reward Codes & Cards (Payment Instruments)
  // ────────────────────────────────────────────────────────────────────────────

  const {
    data: rewardCodes,
    pending: rewardCodesPending,
    status: rewardsCodesStatus,
    error: rewardCodesError,
    execute: getRewardCodes
  } = consumer.getPaymentInstruments({ payment_method_id: 'REWARD_CODE' }, { key: 'REWARD_CODE', immediate: false })

  const {
    data: rewardCards,
    pending: rewardCardsPending,
    status: rewardsCardsStatus,
    error: rewardCardsError,
    execute: getRewardCards
  } = consumer.getPaymentInstruments({ payment_method_id: 'REWARD_CARD' }, {
    immediate: false,
    transform: (rewards) => rewards.map(({ card }) => card)
  })

  /**
   * Fetch both reward codes and reward cards in parallel.
   * - Called on checkout payment step
   * - Returns combined promise
   */
  const getRewards = async () => {
    if (!user.isEmployeeProfile || !hideEmployeePoints) {
      await Promise.all([getRewardCodes(), getRewardCards()])
    }
  }

  /**
   * All available rewards (codes + cards combined).
   * - Flattened array for UI display
   */
  const rewardsAvailable = computed(() => [
    ...rewardCodes.value || [],
    ...rewardCards.value || []
  ])

  /** Are rewards currently loading? */
  const rewardsPending = computed(() => rewardCodesPending.value || rewardCardsPending.value)

  /** Did rewards fetch fail? */
  const rewardsError = computed(() => rewardCodesError.value || rewardCardsError.value)

  /** Have rewards been requested at least once? */
  const rewardsRequested = computed(() => rewardsCodesStatus.value !== 'idle' && rewardsCardsStatus.value !== 'idle')

  // ────────────────────────────────────────────────────────────────────────────
  // Athlete & Credited Consumer Payments
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Athlete payment credit details.
   * - remainingAmount: Credit still available this year
   * - payableAmount: Amount that can be applied to current order
   * - Returns undefined if user is not an athlete
   *
   * **Blocks loyalty rewards** if remainingAmount > 0
   */
  const athletePayment = computed(() => {
    const athleteMethod = cart.paymentMethods.find(({ code }) => code === 'ATHLETES_PAYMENT')

    const remainingAmount = athleteMethod?.additionalData?.remainingAmount ?? 0
    const payableAmount = athleteMethod?.additionalData?.payableAmount ?? 0

    return user.isAthleteProfile ? { remainingAmount, payableAmount } : undefined
  })

  /**
   * Credited consumer (IPA/influencer) payment details.
   * - Similar to athlete credit but for industry pros
   * - Returns undefined if user is not IPA/credited consumer
   *
   * **Blocks loyalty rewards** if remainingAmount > 0
   */
  const creditedConsumerPayment = computed(() => {
    if (!user.creditedConsumerProfileType) return undefined

    const method = cart.paymentMethods.find(({ code }) => code === 'INFLUENCER_CREDIT')

    if (!method) return undefined

    return {
      ...method?.additionalData,
      remainingAmount: method?.additionalData?.remainingAmount ?? 0,
      payableAmount: method?.additionalData?.payableAmount ?? 0,
    }
  })

  /**
   * Are loyalty reward payments allowed?
   * - false: If athlete credit OR influencer credit has remaining balance
   * - true: Rewards can be applied to order
   *
   * **Payment priority:**
   * 1. Athlete/influencer credit (must be used first)
   * 2. Loyalty rewards (vouchers, codes, cards)
   */
  const rewardsPaymentAllowed = computed(() => {
    const athleteBlocked = athletePayment.value && athletePayment.value.remainingAmount > 0
    const creditedConsumerBlocked = creditedConsumerPayment.value && creditedConsumerPayment.value.remainingAmount > 0
    return !(athleteBlocked || creditedConsumerBlocked)
  })

  // ────────────────────────────────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Clear loyalty data from store (called on logout).
   */
  const reset = () => {
    vouchers.value = null
    activities.value = []
    activitiesPage.value = 1
    activitiesResponse.value = undefined
  }

  // Refetch the loyalty vouchers when the cart total changes to determine the correct state of the loyalty rewards component
  watch(() => cart.totals.total, () => {
    if (vouchers.value?.applicableLoyaltyVouchers?.length)
      getVouchers()
  })

  // ────────────────────────────────────────────────────────────────────────────
  // Return (Public API)
  // ────────────────────────────────────────────────────────────────────────────

  return {
    activities,
    activitiesPending,
    /** Athlete credit payment details (remainingAmount, payableAmount) */
    athletePayment,
    /** Available Balance */
    availableBalance,

    /** Credited consumer (IPA) payment details */
    creditedConsumerPayment,

    /** Current reward value based on points (formatted price) */
    currentReward,

    getActivities,
    /** Fetch loyalty vouchers */
    getVouchers,

    /** Fetch reward codes and cards in parallel */
    getRewards,

    /** Whether any voucher has product-exclusion ineligibility */
    hasProductExclusion,

    /** Maximum available amount to redeem */
    highestAvailableVoucher,

    hasMoreActivities,
    loadMoreActivities,
    /** Clear loyalty data (on logout) */
    reset,

    /** All available rewards (codes + cards combined) */
    rewardsAvailable,

    /** Did rewards fetch fail? */
    rewardsError,

    /** Can loyalty rewards be applied? (blocked by athlete/influencer credit) */
    rewardsPaymentAllowed,

    /** Are rewards currently loading? */
    rewardsPending,

    /** Have rewards been requested? (not idle) */
    rewardsRequested,

    /** Vouchers data (points balance, applicable vouchers) */
    vouchers,

    /** Vouchers fetch error */
    vouchersError,

    /** Vouchers fetch status (idle, pending, success, error) */
    vouchersStatus,

    /** Vouchers fetch loading state */
    vouchersPending
  }
})
